import { ROUTES } from '#/config/routes';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import { renderEntityStatusBadge } from '#/modules/commonComponents/table/entityStatusIconHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { searchCustomerFilterOptions } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { PRODUCT_STATUS_FILTER_OPTIONS } from '#/modules/crmMgmt/product/helper/productStatus';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function resolveCustomerCellLabel(customerId, customerLabelCache) {
  if (!customerId) {
    return '—';
  }
  const cached = customerLabelCache?.get(String(customerId));
  return cached || '—';
}

export function buildProductColumns({
  filters = {},
  onFilterChange,
  customerLabelCache,
  canShowEdit = () => true,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.productDetail(row.id),
      getEditPath: (row) => ROUTES.productEdit(row.id),
      canShowEdit,
      size: 80,
      minSize: 80,
    }),
    {
      header: '產品代號',
      accessorKey: 'productNo',
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'productNo',
        label: '產品代號',
        filterValue: filters.productNo,
        onChange: (value) => onFilterChange('productNo', value),
        placeholder: '搜尋產品代號',
      }),
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original?.productNo)} />
      ),
    },
    {
      header: '名稱',
      accessorKey: 'name',
      size: 180,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'name',
          label: '名稱',
          filterValue: filters.name,
          onChange: (value) => onFilterChange('name', value),
          placeholder: '搜尋名稱',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original?.name)} />
      ),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 96,
      enableColumnFilter: true,
      cell: ({ getValue }) => renderEntityStatusBadge(getValue()),
      meta: createFixedMultiFilterMeta({
        key: 'status',
        label: '狀態',
        options: PRODUCT_STATUS_FILTER_OPTIONS,
        filterValue: filters.status,
        onChange: (value) => onFilterChange('status', value),
      }),
    },
    {
      id: 'customer',
      header: '客戶',
      accessorFn: (row) => resolveCustomerCellLabel(row.customerId, customerLabelCache),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={resolveCustomerCellLabel(row.original?.customerId, customerLabelCache)}
        />
      ),
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'customerId',
          label: '客戶',
          searchOptions: searchCustomerFilterOptions,
          filterValue: filters.customerId,
          onChange: (value) => onFilterChange('customerId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(customerLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(customerLabelCache, options, values);
          },
        }),
      },
    },
    {
      header: '客戶料號',
      accessorKey: 'customerProductNo',
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'customerProductNo',
          label: '客戶料號',
          filterValue: filters.customerProductNo,
          onChange: (value) => onFilterChange('customerProductNo', value),
          placeholder: '搜尋客戶料號',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original?.customerProductNo)} />
      ),
    },
    {
      header: '銷售單位',
      accessorKey: 'salesUnitName',
      size: 100,
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original?.salesUnitName)} />
      ),
    },
    {
      header: '備註',
      accessorKey: 'remark',
      size: 200,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original?.remark)} />
      ),
    },
  ];
}
